import { useEffect, useMemo, useState } from "react";
import { Public } from "@/lib/api";
import { Link, navigate, useRoute } from "@/lib/router";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Award, Search, MapPin, Building2, Calendar, Banknote, Lock, Trophy, ArrowRight,
} from "lucide-react";
import { Pagination } from "@/components/Pagination";
import { SkeletonList } from "@/components/ui/skeleton";

const PROVINCES = [
  "北京","天津","河北","山西","内蒙古","辽宁","吉林","黑龙江","上海","江苏","浙江",
  "安徽","福建","江西","山东","河南","湖北","湖南","广东","广西","海南","重庆",
  "四川","贵州","云南","陕西","甘肃","青海","宁夏","新疆","西藏",
];

export function WinnersListPage() {
  const route = useRoute();
  const [cats, setCats] = useState<any[]>([]);
  const [data, setData] = useState<{ items: any[]; page: number; total?: number } | null>(null);

  // 受控筛选条件（不立刻发起请求，点击「检索」时同步到 URL）
  const [q, setQ] = useState<string>(route.query.q || "");
  const [bidder, setBidder] = useState<string>(route.query["f.bidder"] || "");
  const [winner, setWinner] = useState<string>(route.query["f.winner"] || "");
  const [province, setProvince] = useState<string>(route.query["f.province"] || "");
  const [year, setYear] = useState<string>(route.query["f.year"] || "");
  const [category, setCategory] = useState<string>(route.query.category || "");
  // 中标阶段：candidate（中标候选人）/ official（中标公告/结果）
  const [stage, setStage] = useState<string>(route.query.stage || "official");

  useEffect(() => { Public.categories().then(setCats); }, []);

  // URL → 列表
  useEffect(() => {
    const page = Number(route.query.page || 1);
    const params: Record<string, any> = { page, page_size: 20 };
    const st = route.query.stage || "official";
    params["f.type"] = st === "candidate" ? "中标候选人" : "中标公告";
    if (route.query.q) params.q = route.query.q;
    if (route.query.category) params.category = route.query.category;
    (["bidder","winner","province","year"] as const).forEach((k) => {
      const v = route.query[`f.${k}`];
      if (v) params[`f.${k}`] = v;
    });
    Public.projects(params).then(setData).catch(() => setData({ items: [], page }));
  }, [route.query]);

  const apply = () => {
    const qs: Record<string, string> = {};
    if (q) qs.q = q;
    if (category) qs.category = category;
    if (bidder) qs["f.bidder"] = bidder;
    if (winner) qs["f.winner"] = winner;
    if (province) qs["f.province"] = province;
    if (year) qs["f.year"] = year;
    if (stage && stage !== "official") qs.stage = stage;
    navigate("/winners", qs);
  };
  const reset = () => {
    setQ(""); setBidder(""); setWinner(""); setProvince(""); setYear(""); setCategory(""); setStage("official");
    navigate("/winners");
  };

  const stats = useMemo(() => {
    const items = data?.items || [];
    const byProvince: Record<string, number> = {};
    let totalAmount = 0; // 单位：亿元
    items.forEach((p) => {
      const pr = p.dynamic_fields?.province;
      if (pr) byProvince[pr] = (byProvince[pr] || 0) + 1;
      // 优先取数值字段 amount_wan（万元），再回退到字符串 amount（"X 亿"）
      const wan = p.dynamic_fields?.amount_wan;
      if (typeof wan === "number" && isFinite(wan) && wan > 0) {
        totalAmount += wan / 10000;
      } else {
        const a = p.dynamic_fields?.amount as string | undefined;
        if (a) {
          const m = a.match(/([\d.]+)\s*亿/);
          if (m && m[1]) totalAmount += parseFloat(m[1]);
        }
      }
    });
    const topProv = Object.entries(byProvince).sort((a, b) => b[1] - a[1]).slice(0, 6);
    return { count: items.length, byProvince: topProv, totalAmount };
  }, [data]);

  const page = Number(route.query.page || 1);

  return (
    <div>
      {/* Hero */}
      <section className="border-b bg-gradient-to-br from-amber-500/15 via-amber-500/5 to-transparent">
        <div className="container mx-auto px-4 py-10 md:py-14 max-w-6xl">
          <div className="flex items-center gap-3 mb-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500 text-white shadow-lg shadow-amber-500/30">
              <Trophy className="h-5 w-5" />
            </span>
            <Badge variant="secondary" className="rounded-full">中标快讯</Badge>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">中标公告 · 候选人公示 · 结果公示</h1>
          <p className="text-muted-foreground mt-3 max-w-3xl">
            聚合主流国央企招标方的中标信息，支持按招标方、中标人、地区、年份多维筛选。开通会员查看完整中标人详情与中标金额。
          </p>

          {/* 一站式搜索条 */}
          <div className="mt-6 grid md:grid-cols-[1fr_auto] gap-3">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") apply(); }}
                placeholder="搜索项目名称、中标人、招标方关键词…"
                className="pl-10 h-11"
              />
            </div>
            <Button size="lg" onClick={apply} className="h-11 px-6">
              检索 <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          {/* 概览数据 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <StatBox icon={<Award />} label="本页中标" value={`${stats.count}`} />
            <StatBox icon={<Banknote />} label="累计金额" value={stats.totalAmount > 0 ? `${stats.totalAmount.toFixed(1)} 亿+` : "—"} />
            <StatBox icon={<Building2 />} label="覆盖招标方" value={`${cats.filter(c => !c.parent_id).length}+`} />
            <StatBox icon={<MapPin />} label="覆盖省份" value={`${stats.byProvince.length || "—"}`} />
          </div>
        </div>
      </section>

      {/* Body */}
      <div className="container mx-auto px-4 py-8 max-w-6xl grid lg:grid-cols-[280px_1fr] gap-6">
        {/* 筛选侧栏 */}
        <aside>
          <Card className="lg:sticky lg:top-20">
            <CardContent className="p-4 space-y-4">
              <h2 className="font-semibold text-sm">高级筛选</h2>

              <Field label="中标阶段">
                <select className="w-full h-9 rounded-md border bg-transparent px-2 text-sm"
                  value={stage} onChange={(e) => setStage(e.target.value)}>
                  <option value="all">全部（候选人 + 中标公告）</option>
                  <option value="official">中标公告 / 结果</option>
                  <option value="candidate">中标候选人公示</option>
                </select>
              </Field>

              <Field label="招标方栏目">
                <select className="w-full h-9 rounded-md border bg-transparent px-2 text-sm"
                  value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="">全部栏目</option>
                  {cats.filter((c: any) => !c.parent_id).map((c: any) => (
                    <option key={c.id} value={c.slug}>{c.name}</option>
                  ))}
                </select>
              </Field>

              <Field label="省份"><select className="w-full h-9 rounded-md border bg-transparent px-2 text-sm"
                value={province} onChange={(e) => setProvince(e.target.value)}>
                <option value="">全部省份</option>
                {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select></Field>

              <Field label="年份"><select className="w-full h-9 rounded-md border bg-transparent px-2 text-sm"
                value={year} onChange={(e) => setYear(e.target.value)}>
                <option value="">全部年份</option>
                {[2026, 2025, 2024, 2023, 2022].map((y) => <option key={y} value={y}>{y}</option>)}
              </select></Field>

              <Field label="招标方关键字">
                <Input value={bidder} onChange={(e) => setBidder(e.target.value)} placeholder="如：国网江苏" />
              </Field>

              <Field label="中标人关键字">
                <Input value={winner} onChange={(e) => setWinner(e.target.value)} placeholder="如：金风科技" />
              </Field>

              <div className="flex gap-2 pt-2">
                <Button size="sm" className="flex-1" onClick={apply}>应用筛选</Button>
                <Button size="sm" variant="outline" onClick={reset}>重置</Button>
              </div>

              {stats.byProvince.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-xs">本页省份分布</Label>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {stats.byProvince.map(([pr, n]) => (
                      <button key={pr} type="button"
                        onClick={() => { setProvince(pr); }}
                        className="text-xs rounded-md border px-2 py-1 hover:border-primary hover:text-primary transition">
                        {pr} <span className="text-muted-foreground">{n}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </aside>

        {/* 列表 */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-muted-foreground">
              {data ? <>共 <span className="text-foreground font-medium">{data.items.length}</span> 条中标信息</> : "加载中..."}
            </div>
            <div className="text-xs text-muted-foreground">第 {page} 页</div>
          </div>

          <ul className="divide-y rounded-md border bg-card">
            {!data && <li className="p-0"><SkeletonList rows={8} /></li>}
            {(data?.items || []).map((p) => {
              const df = p.dynamic_fields || {};
              return (
                <li key={p.id} className="px-4 py-3 hover:bg-muted/40 transition">
                  <Link to={`/p/${p.slug}`} className="flex items-start gap-3 min-w-0">
                    {p.locked
                      ? <Badge variant="outline" className="shrink-0 mt-0.5 border-amber-500/50 text-amber-700 text-[10px] px-1.5 h-5"><Lock className="h-3 w-3 mr-0.5" />VIP</Badge>
                      : <span className="shrink-0 inline-block h-1.5 w-1.5 rounded-full bg-amber-500 mt-2" />}
                    <span className="flex-1 min-w-0 text-sm leading-snug line-clamp-2 group-hover:text-primary hover:text-primary">{p.title}</span>
                    <span className="hidden md:inline-flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                      {df.province && <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{df.province}</span>}
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0 inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {p.published_at ? new Date(p.published_at).toLocaleDateString() : "—"}
                    </span>
                  </Link>
                </li>
              );
            })}
            {data && data.items.length === 0 && (
              <li className="py-16 text-center space-y-3">
                <p className="text-sm text-muted-foreground">没有匹配的中标信息</p>
                <Button size="sm" variant="outline" onClick={reset}>清除筛选</Button>
              </li>
            )}
          </ul>

          {data && (data.total ?? 0) > 0 && (
            <div className="mt-6">
              <Pagination
                page={page}
                total={data.total ?? 0}
                pageSize={20}
                onChange={(p) => navigate("/winners", { ...route.query, page: p })}
              />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function StatBox({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border bg-card/70 backdrop-blur px-4 py-3">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 [&>svg]:h-4 [&>svg]:w-4">
        {icon}
      </span>
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-base md:text-lg font-bold truncate">{value}</div>
      </div>
    </div>
  );
}

function Cell(_: { icon: React.ReactNode; label: string; value?: string; highlight?: boolean }) {
  return null; // 旧版宽列单元格已不再使用，保留兼容签名
}
void Cell;
